import { io } from 'socket.io-client'
import { API_BASE_URL } from '../config/api'
import { secureChannel } from './api'
import { loadSiteTheme } from '../siteBrand'

// Socket base = the API host WITHOUT the /api path suffix — socket.io mounts at
// the server root on `path: '/socket.io'` (see server/server.js).
const SOCKET_URL =
  import.meta.env.VITE_SOCKET_URL || API_BASE_URL.replace(/\/api\/?$/, '')

let socket = null
let socketToken = null // which token the current socket authenticated with
let adminStaffId = null // which staff account this socket belongs to
let adminOwnerId = null // ...or which owner account
let themeTimer = null

// Admin-side Socket.IO singleton. Mirrors the public frontend's client
// (frontend/src/apiservices/authService.js) on purpose, including the transport
// pin: websocket only. The API runs as several processes (pm2 cluster); one
// websocket is one connection to one process, while long-polling's separate
// requests land on different processes and fail with "Session ID unknown".
// (Polling was pinned in 2026-08 for a "frames dropped after upgrade" proxy
// symptom that was never root-caused; the API vhost proxies websockets with
// `upgrade=websocket`. Plan: docs/plans/2026-10-21-cluster-mode-readiness.md.)
export const getAdminSocket = (token) => {
  if (!token) return null

  // Same session → reuse. Recreating the socket would tear down listeners other
  // components registered on it.
  if (socket && socketToken === token) {
    if (!socket.connected) socket.connect()
    return socket
  }

  // Different token (re-login, role switch) → start fresh.
  if (socket) {
    socket.disconnect()
    socket = null
  }

  socketToken = token
  socket = io(SOCKET_URL, {
    // function form: re-read at every (re)connect, adds the secure-channel sid
    auth: secureChannel.socketAuth({ token }),
    path: '/socket.io',
    transports: ['websocket'],
    withCredentials: true,
    forceNew: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  })

  // Decrypt pushes before any listener runs (secure channel).
  secureChannel.secureSocket(socket)

  // Owner Panel saved a setting: repaint the theme, no refresh. After 2–8 s:
  // past each API process's 2 s cache, and spread so pages don't all ask at
  // once. Plan: docs/plans/2026-10-28-owner-panel-live-push.md
  socket.on('siteSettings', () => {
    clearTimeout(themeTimer)
    themeTimer = setTimeout(loadSiteTheme, 2000 + Math.random() * 6000)
  })

  /* Fires on the first connect AND on every reconnect, so the socket always
     (re)joins its `admin_<staffId>` room after a drop. Without this join the
     server's forceLogout push had nowhere to land and an upline lock only
     surfaced when the next request 401'd — i.e. after navigating somewhere. */
  socket.on('connect', () => {
    if (adminStaffId || adminOwnerId) {
      socket.emit('authenticateAdmin', { staffId: adminStaffId, ownerId: adminOwnerId })
    }
  })

  // Upline locked this account (or one above it): the server already revoked
  // the token, this only makes the logout instant. App.jsx owns the redux
  // logout + redirect. The server does not verify the room join, so only act on
  // a push addressed to this socket's own account.
  socket.on('forceLogout', (data) => {
    const mine = (adminStaffId && String(data?.staffId) === adminStaffId)
      || (adminOwnerId && String(data?.ownerId) === adminOwnerId)
    if (!mine) return
    window.dispatchEvent(new CustomEvent('forceLogout', { detail: data }))
  })

  socket.on('connect_error', (error) => {
    console.error('Admin socket connect error:', error?.message || error)
  })

  return socket
}

// Who this admin session is. Set before/after getAdminSocket in either order —
// useOwnerProfileSocket opens the same singleton without an id, so the id is
// kept here rather than passed in, and a socket that is already up is joined
// straight away instead of waiting for the next reconnect.
export const setAdminSessionId = ({ staffId, ownerId } = {}) => {
  adminStaffId = staffId ? String(staffId) : null
  adminOwnerId = ownerId ? String(ownerId) : null
  if ((adminStaffId || adminOwnerId) && socket?.connected) {
    socket.emit('authenticateAdmin', { staffId: adminStaffId, ownerId: adminOwnerId })
  }
}

// ── Owner-profile liveness ──────────────────────────────────────────────────
// Once the socket is actually delivering profile snapshots, the REST endpoint
// must stop being polled — otherwise every route change still fires
// GET /admin/owner/profile and the socket is just extra traffic on top.
// useProfileRefresh consults this and steps aside while the push is healthy;
// if the socket drops (no snapshot for STALE_AFTER_MS) this goes false again
// and the REST path automatically takes over, so a dead socket never leaves the
// dashboard frozen.
const STALE_AFTER_MS = 6000 // 3 missed 2s ticks
let lastOwnerProfileAt = 0

export const markOwnerProfileLive = () => {
  lastOwnerProfileAt = Date.now()
}

export const isOwnerProfileLive = () =>
  lastOwnerProfileAt > 0 && Date.now() - lastOwnerProfileAt < STALE_AFTER_MS

// Ask the server for an immediate tick instead of waiting out the 2s interval.
// Called right after the admin MOVES money, where "in up to 2s" is not good
// enough. Returns false when there is no live socket to ask, so the caller can
// fall back to REST.
export const requestOwnerProfileRefresh = () => {
  if (!socket || !socket.connected || !isOwnerProfileLive()) return false
  socket.emit('refreshOwnerProfile')
  return true
}

export const disconnectAdminSocket = () => {
  if (socket) {
    socket.disconnect()
    socket = null
  }
  socketToken = null
  lastOwnerProfileAt = 0
}

export default getAdminSocket
